import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, MaskedLinesInView, Reveal } from "../components/Reveal";
import { SERVICES, INDUSTRIES } from "../lib/site";
import { IMG } from "../lib/images";

const PROCESS = [
  ["Intake", "We learn your roles, shifts, pay bands and culture in a single working session."],
  ["Source & screen", "We pull from our vetted talent pool, interview and verify — background, references, right-to-work."],
  ["Deploy", "Shift-ready workers arrive onboarded, compliant and briefed on your expectations."],
  ["Manage", "We track attendance, performance and retention, adjusting the plan in real time."],
];

export default function Services() {
  return (
    <div data-testid="services-page" className="pt-[72px]">
      <section className="mx-auto max-w-[1500px] px-6 md:px-10 pt-20 md:pt-28 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-9">
          <p className="overline text-flame mb-6">Services</p>
          <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[12vw] lg:text-[7.5vw]">
            <MaskedLines lines={["Staffing, payroll", <span key="2" className="text-cobalt">& HR, solved.</span>]} delay={0.2} />
          </h1>
        </div>
        <div className="lg:col-span-3">
          <p className="text-muted-foreground">From a single fill to full payroll and PEO management, we scale to whatever your business needs.</p>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 md:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
          {SERVICES.map((s, i) => (
            <Reveal key={s.n} delay={(i % 3) * 0.07}>
              <div className="group border-b border-r border-border p-8 md:p-10 h-full hover:bg-cobalt hover:text-white transition-colors duration-400">
                <span className="font-mono text-sm text-flame">{s.n}</span>
                <h3 className="font-display uppercase tracking-tight text-3xl mt-8 leading-[0.95]">{s.title}</h3>
                <p className="text-sm mt-4 text-muted-foreground group-hover:text-white/75 transition-colors">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ink text-bone">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-24 md:py-32">
          <p className="overline text-flame mb-5">Industries we serve</p>
          <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-4xl md:text-6xl mb-14 max-w-3xl">
            <MaskedLinesInView lines={["Talent for every", "corner of your", "operation"]} />
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 border-t border-l border-bone/20">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind} delay={(i % 3) * 0.06}>
                <div className="group border-b border-r border-bone/20 p-6 md:p-8 flex items-center gap-4 hover:bg-flame transition-colors duration-300">
                  <span className="font-mono text-xs text-flame group-hover:text-white">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display uppercase tracking-tight text-xl md:text-2xl">{ind}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative h-[55vh] overflow-hidden">
        <img src={IMG.manufacturing} alt="Warehouse operations" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/40" />
      </section>

      <section className="mx-auto max-w-[1500px] px-6 md:px-10 py-24 md:py-32">
        <p className="overline text-cobalt mb-5">How it works</p>
        <h2 className="font-display uppercase tracking-tighter leading-[0.9] text-4xl md:text-6xl mb-14">Four steps to a full floor</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
          {PROCESS.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.07}>
              <div className="flex gap-6 border-t border-border py-8">
                <span className="font-display text-5xl text-flame tracking-tighter leading-none">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-display uppercase tracking-tight text-2xl">{t}</h3>
                  <p className="text-muted-foreground mt-2">{d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <Link to="/employers" className="inline-flex items-center gap-2 mt-12 bg-cobalt text-white px-6 py-3 rounded-full overline hover:bg-flame transition-colors duration-300">
            Request workers <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
